import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, MapPin, User, Heart, ShoppingBag, Menu, X, ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useShop } from "@/context/ShopContext";
import Logo from "@/components/Logo";

const MARTS = [
  { slug: "home-mart", name: "Home Mart", color: "#4A7C59", cats: ["Grocery", "Beverages", "Snacks", "Personal Care", "Home Essentials"] },
  { slug: "style-mart", name: "Style Mart", color: "#C77F76", cats: ["Women", "Men", "Kids", "Accessories", "Imitation Jewellery"] },
  { slug: "wood-mart", name: "Wood Mart", color: "#A45C40", cats: ["Living Room", "Bedroom", "Dining", "Office"] },
  { slug: "tech-mart", name: "Tech Mart", color: "#00A8E8", cats: ["Mobiles & Accessories", "TVs & Entertainment", "Home Appliances", "Computers", "Smart Home", "Gaming"] },
];

export default function Header() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { cart, wishlist } = useShop();
  const [q, setQ] = useState("");
  const [sugg, setSugg] = useState([]);
  const [openMart, setOpenMart] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [ann, setAnn] = useState([]);
  const boxRef = useRef();

  useEffect(() => { api.get("/announcements").then((r) => setAnn(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (q.length < 2) { setSugg([]); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get(`/products/suggest?q=${encodeURIComponent(q)}`); setSugg(data); } catch {}
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setSugg([]); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const doSearch = (e) => {
    e.preventDefault();
    if (q.trim()) { navigate(`/shop?search=${encodeURIComponent(q.trim())}`); setSugg([]); }
  };

  return (
    <header className="sticky top-0 z-50">
      {/* Announcement bar */}
      <div className="bg-midnight text-white overflow-hidden">
        <div className="flex whitespace-nowrap py-2 animate-marquee">
          {[...ann, ...ann].map((a, i) => (
            <span key={i} data-testid={i === 0 ? "announcement-bar" : undefined} className="mx-8 text-xs md:text-sm font-medium tracking-wide flex items-center gap-2">
              <span className="h-1 w-1 rounded-full bg-gold" /> {a}
            </span>
          ))}
        </div>
      </div>

      {/* Main header */}
      <div className="glass border-b border-black/5">
        <div className="mx-auto max-w-[1400px] px-4 md:px-8">
          <div className="flex items-center gap-4 py-4">
            <button className="lg:hidden" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn" aria-label="Menu">
              <Menu className="h-6 w-6" />
            </button>
            <Link to="/" data-testid="logo-link" className="shrink-0">
              <Logo className="h-11 md:h-14 w-auto" />
            </Link>

            {/* Search */}
            <div className="relative hidden md:block flex-1 max-w-2xl mx-auto" ref={boxRef}>
              <form onSubmit={doSearch}>
                <div className="flex items-center rounded-full border border-border bg-white px-5 py-3 focus-within:border-gold transition-colors">
                  <Search className="h-4 w-4 text-muted-foreground" />
                  <input
                    data-testid="search-input"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search products, brands and categories"
                    className="ml-3 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                  />
                </div>
              </form>
              {sugg.length > 0 && (
                <div data-testid="search-suggestions" className="absolute mt-2 w-full rounded-2xl border border-border bg-white shadow-xl overflow-hidden z-50">
                  {sugg.map((s) => (
                    <Link key={s.slug} to={`/product/${s.slug}`} onClick={() => setSugg([])}
                      className="flex items-center gap-3 px-4 py-3 hover:bg-secondary transition-colors">
                      <img src={s.images?.[0]} alt="" className="h-10 w-10 rounded-lg object-cover" />
                      <span className="text-sm font-medium text-midnight line-clamp-1">{s.name}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Right icons */}
            <div className="flex items-center gap-1 md:gap-3 ml-auto">
              <button className="hidden md:flex items-center gap-1 text-xs text-muted-foreground hover:text-midnight" data-testid="location-btn">
                <MapPin className="h-5 w-5" /> <span className="hidden xl:inline">Deliver to India</span>
              </button>
              <div className="relative group">
                <button className="flex items-center gap-1 p-2" data-testid="account-menu-btn">
                  <User className="h-5 w-5" />
                </button>
                <div className="absolute right-0 top-full hidden group-hover:block pt-2 z-50">
                  <div className="w-52 rounded-2xl border border-border bg-white shadow-xl p-2">
                    {user && user.user_id ? (
                      <>
                        <div className="px-3 py-2 text-sm font-semibold text-midnight truncate">{user.name}</div>
                        <Link to="/account" className="block rounded-lg px-3 py-2 text-sm hover:bg-secondary" data-testid="menu-account">My Account</Link>
                        <Link to="/account/orders" className="block rounded-lg px-3 py-2 text-sm hover:bg-secondary">Orders</Link>
                        <Link to="/wishlist" className="block rounded-lg px-3 py-2 text-sm hover:bg-secondary">Wishlist</Link>
                        {user.role === "admin" && <Link to="/admin" className="block rounded-lg px-3 py-2 text-sm font-semibold text-gold-dark hover:bg-secondary" data-testid="menu-admin">Admin Panel</Link>}
                        {user.role === "vendor" && <Link to="/vendor" className="block rounded-lg px-3 py-2 text-sm font-semibold text-mart-wood hover:bg-secondary" data-testid="menu-vendor">Vendor Portal</Link>}
                        <button onClick={logout} className="block w-full text-left rounded-lg px-3 py-2 text-sm text-red-600 hover:bg-secondary" data-testid="logout-btn">Logout</button>
                      </>
                    ) : (
                      <>
                        <Link to="/login" className="block rounded-lg px-3 py-2 text-sm font-semibold hover:bg-secondary" data-testid="menu-login">Login</Link>
                        <Link to="/register" className="block rounded-lg px-3 py-2 text-sm hover:bg-secondary" data-testid="menu-register">Register</Link>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <Link to="/wishlist" className="relative p-2" data-testid="wishlist-link">
                <Heart className="h-5 w-5" />
                {wishlist.length > 0 && <span className="absolute -right-0 -top-0 grid h-4 w-4 place-items-center rounded-full bg-gold text-[10px] font-bold text-midnight">{wishlist.length}</span>}
              </Link>
              <Link to="/cart" className="relative p-2" data-testid="cart-link">
                <ShoppingBag className="h-5 w-5" />
                {cart.count > 0 && <span className="absolute -right-0 -top-0 grid h-4 w-4 place-items-center rounded-full bg-gold text-[10px] font-bold text-midnight" data-testid="cart-count">{cart.count}</span>}
              </Link>
            </div>
          </div>

          {/* Mobile search */}
          <form onSubmit={doSearch} className="md:hidden pb-3">
            <div className="flex items-center rounded-full border border-border bg-white px-4 py-2.5">
              <Search className="h-4 w-4 text-muted-foreground" />
              <input data-testid="search-input-mobile" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products, brands..." className="ml-3 w-full bg-transparent text-sm outline-none" />
            </div>
          </form>

          {/* Nav */}
          <nav className="hidden lg:flex items-center gap-1 pb-2">
            <Link to="/" className="px-4 py-2 text-sm font-semibold hover:text-gold-dark" data-testid="nav-home">Home</Link>
            {MARTS.map((m) => (
              <div key={m.slug} className="relative" onMouseEnter={() => setOpenMart(m.slug)} onMouseLeave={() => setOpenMart(null)}>
                <Link to={`/${m.slug}`} data-testid={`nav-${m.slug}`} className="flex items-center gap-1 px-4 py-2 text-sm font-semibold hover:text-gold-dark">
                  {m.name} <ChevronDown className="h-3.5 w-3.5" />
                </Link>
                {openMart === m.slug && (
                  <div className="absolute left-0 top-full w-64 rounded-2xl border border-border bg-white shadow-xl p-3 z-50">
                    <div className="mb-2 h-1 w-12 rounded-full" style={{ background: m.color }} />
                    {m.cats.map((c) => (
                      <Link key={c} to={`/${m.slug}?category=${encodeURIComponent(c)}`} className="block rounded-lg px-3 py-2 text-sm text-midnight hover:bg-secondary">{c}</Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <Link to="/offers" className="px-4 py-2 text-sm font-semibold hover:text-gold-dark" data-testid="nav-offers">Offers</Link>
            <Link to="/new-arrivals" className="px-4 py-2 text-sm font-semibold hover:text-gold-dark">New Arrivals</Link>
            <Link to="/brands" className="px-4 py-2 text-sm font-semibold hover:text-gold-dark">Brands</Link>
            <Link to="/stores" className="px-4 py-2 text-sm font-semibold hover:text-gold-dark">Stores</Link>
          </nav>
        </div>
      </div>

      {/* Mobile slide-out */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-[80%] max-w-sm bg-white p-6 overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <Logo className="h-7 w-auto" />
              <button onClick={() => setMobileOpen(false)} data-testid="mobile-menu-close"><X className="h-6 w-6" /></button>
            </div>
            <div className="space-y-1">
              <Link to="/" onClick={() => setMobileOpen(false)} className="block py-2 font-semibold">Home</Link>
              {MARTS.map((m) => (
                <div key={m.slug}>
                  <Link to={`/${m.slug}`} onClick={() => setMobileOpen(false)} className="flex items-center gap-2 py-2 font-semibold">
                    <span className="h-2 w-2 rounded-full" style={{ background: m.color }} /> {m.name}
                  </Link>
                </div>
              ))}
              <Link to="/offers" onClick={() => setMobileOpen(false)} className="block py-2 font-semibold">Offers</Link>
              <Link to="/new-arrivals" onClick={() => setMobileOpen(false)} className="block py-2 font-semibold">New Arrivals</Link>
              <Link to="/brands" onClick={() => setMobileOpen(false)} className="block py-2 font-semibold">Brands</Link>
              <Link to="/stores" onClick={() => setMobileOpen(false)} className="block py-2 font-semibold">Stores</Link>
              <Link to="/about" onClick={() => setMobileOpen(false)} className="block py-2 font-semibold">About</Link>
              <Link to="/become-vendor" onClick={() => setMobileOpen(false)} className="block py-2 font-semibold">Become a Vendor</Link>
              <Link to="/franchise" onClick={() => setMobileOpen(false)} className="block py-2 font-semibold">Franchise</Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
